import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Smartphone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { SITE, planLabel } from "@/data/site";

export default function ThankYou() {
  const { state } = useLocation();
  const { user, refreshMe } = useAuth();
  const plan = state?.plan;
  const username = state?.username || user?.name;

  useEffect(() => {
    document.title = "Thank you — Warranty_Vault pro";
    refreshMe();
  }, [refreshMe]);

  const ownedLabel = user && user.plan_type && user.plan_type !== "free" ? planLabel(user) : null;

  return (
    <main className="relative min-h-screen pt-28" data-testid="thank-you-page">
      <div className="vault-grid-bg grain absolute inset-x-0 top-0 h-96" />
      <div className="relative mx-auto max-w-2xl px-5 pb-10 text-center sm:px-8">
        <Reveal>
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl border border-sage/30 bg-sage/10 text-sage">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <p className="eyebrow mt-6">Request received</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            Thanks{username ? `, ${username}` : ""} — we've got your request
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            {plan ? (
              <>Your request for <span className="font-semibold text-foreground">{plan}</span> is in. </>
            ) : (
              <>Your request is in. </>
            )}
            Complete your payment in the secure Stripe tab that just opened. Once it's done,
            your plan will be linked to your Warranty_Vault pro account.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          {ownedLabel && (
            <div data-testid="owned-plan-badge" className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-sage/40 bg-sage/10 px-4 py-2 text-sm font-semibold capitalize text-sage">
              <CheckCircle2 className="h-4 w-4" /> Active plan: {ownedLabel}
            </div>
          )}
          <div className="mt-2 flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/10 p-4 text-left">
            <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
            <p className="text-sm leading-relaxed text-foreground/90">
              Warranty_Vault pro is a mobile app — after payment, open the app on your phone or tablet
              and sign in with the same email and username to see your plan.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              data-testid="thankyou-home"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
            >
              Back to home <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`mailto:${SITE.supportEmail}`}
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground/90 transition-colors hover:border-gold/40 hover:text-gold"
            >
              Need help?
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
